import axios from 'axios';
import { dispatchDriverToast } from '../hooks/useDriverToast';

export function getDriverApiBaseUrl() {
  return process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
}

function getDriverToken() {
  return localStorage.getItem('driver_token');
}

function getDriverRefreshToken() {
  return localStorage.getItem('driver_refresh_token');
}

function dispatchDriverLogout() {
  window.dispatchEvent(new Event('driver_logout'));
}

let isRefreshing = false;
let pendingRequests = [];

function processQueue(error, newAccessToken = null) {
  pendingRequests.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      if (!config.headers) config.headers = {};
      config.headers['Authorization'] = `Bearer ${newAccessToken}`;
      config._retry = true;
      resolve(apiClient(config));
    }
  });
  pendingRequests = [];
}

function refreshDriverAccess() {
  const refresh = getDriverRefreshToken();
  if (!refresh) return Promise.reject(new Error('No refresh token'));
  const base = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  return axios
    .post(`${base}/auth/token/refresh/`, { refresh }, {
      headers: { 'Content-Type': 'application/json' },
    })
    .then((res) => {
      const access = res.data?.access;
      if (!access) return Promise.reject(new Error('No access in refresh response'));
      localStorage.setItem('driver_token', access);
      return access;
    });
}

export const apiClient = axios.create({
  baseURL: getDriverApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = getDriverToken();
    if (token) {
      if (!config.headers) config.headers = {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (err) => Promise.reject(err)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
      if (error.response?.status >= 500) {
        dispatchDriverToast({ type: 'error', message: 'Error del servidor. Intenta de nuevo.' });
      } else if (!error.response) {
        dispatchDriverToast({ type: 'warning', message: 'Sin conexión. Revisa tu internet.' });
      }
      return Promise.reject(error);
    }

    const refreshToken = getDriverRefreshToken();
    if (!refreshToken) {
      localStorage.removeItem('driver_token');
      dispatchDriverLogout();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingRequests.push({ resolve, reject, config: originalRequest });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const access = await refreshDriverAccess();
      processQueue(null, access);
      if (!originalRequest.headers) originalRequest.headers = {};
      originalRequest.headers['Authorization'] = `Bearer ${access}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      localStorage.removeItem('driver_token');
      localStorage.removeItem('driver_refresh_token');
      dispatchDriverLogout();
      dispatchDriverToast({ type: 'error', message: 'Sesión expirada. Inicia sesión de nuevo.' });
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);
